import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";

export interface ChecklistItem {
  key: string;
  label: string;
  description: string;
  required: boolean;
  completed: boolean;
  route?: string;
}

const CHECKLIST_DEFINITIONS: Omit<ChecklistItem, "completed">[] = [
  {
    key: "brand_configured",
    label: "Configure sua marca",
    description: "Adicione logo, cores ou dados de contato da empresa",
    required: true,
    route: "/app/configuracoes?tab=empresa",
  },
  {
    key: "company_confirmed",
    label: "Confirme seus dados",
    description: "Verifique CNPJ, telefone e endereço",
    required: true,
    route: "/app/configuracoes?tab=empresa",
  },
  {
    key: "pipeline_configured",
    label: "Configure o pipeline",
    description: "Defina as etapas da sua venda",
    required: true,
    route: "/app/configuracoes?tab=pipeline",
  },
  {
    key: "products_added",
    label: "Cadastre seus produtos",
    description: "Adicione kits e serviços ao catálogo",
    required: false,
    route: "/app/configuracoes?tab=produtos",
  },
  {
    key: "team_invited",
    label: "Convide sua equipe",
    description: "Adicione vendedores e técnicos",
    required: false,
    route: "/app/configuracoes?tab=equipe",
  },
  {
    key: "first_lead_created",
    label: "Crie seu primeiro lead",
    description: "Cadastre um contato ou oportunidade",
    required: true,
    route: "/app/crm",
  },
  {
    key: "first_proposal_sent",
    label: "Envie uma proposta",
    description: "Gere e envie sua primeira proposta",
    required: false,
    route: "/app/crm",
  },
  {
    key: "first_deal_won",
    label: "Feche seu primeiro negócio",
    description: "Marque um deal como ganho",
    required: false,
    route: "/app/crm",
  },
];

export function useOnboardingChecklist() {
  const { currentTenant } = useAuth();
  const queryClient = useQueryClient();

  const { data: state, isLoading } = useQuery({
    queryKey: ["onboarding-checklist", currentTenant?.id],
    queryFn: async () => {
      const { data, error } = await supabase.rpc("get_onboarding_state");
      if (error) {
        console.error("useOnboardingChecklist: erro ao buscar estado", error);
        return null;
      }
      return data as any;
    },
    enabled: !!currentTenant?.id,
    staleTime: 30 * 1000,
  });

  const checklistItems: ChecklistItem[] = CHECKLIST_DEFINITIONS.map((def) => ({
    ...def,
    completed: state?.state?.checklist_items?.[def.key] === true,
  }));

  const completedCount = checklistItems.filter((i) => i.completed).length;
  const totalCount = checklistItems.length;
  const progressPercent = Math.round((completedCount / totalCount) * 100);
  const isActivated = !!state?.state?.activated_at;
  const isFirstDealWon = !!state?.state?.first_deal_won_at;
  const widgetDismissed = !!state?.state?.widget_dismissed;
  const widgetMinimized = !!state?.state?.widget_minimized;

  const markItem = useMutation({
    mutationFn: async (itemKey: string) => {
      const { error } = await supabase.rpc("update_checklist_item", {
        p_item_key: itemKey,
        p_value: true,
      });
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["onboarding-checklist", currentTenant?.id],
      });
    },
  });

  const dismissWidget = useMutation({
    mutationFn: async () => {
      const { error } = await supabase
        .from("tenant_onboarding_state")
        .update({ widget_dismissed: true } as any)
        .eq("tenant_id", currentTenant?.id ?? "");
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["onboarding-checklist", currentTenant?.id],
      });
    },
  });

  const toggleMinimize = useMutation({
    mutationFn: async (minimized: boolean) => {
      const { error } = await supabase
        .from("tenant_onboarding_state")
        .update({ widget_minimized: minimized } as any)
        .eq("tenant_id", currentTenant?.id ?? "");
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["onboarding-checklist", currentTenant?.id],
      });
    },
  });

  return {
    checklistItems,
    completedCount,
    totalCount,
    progressPercent,
    isActivated,
    isFirstDealWon,
    widgetDismissed,
    widgetMinimized,
    isLoading,
    state,
    markItem: (key: string) => markItem.mutate(key),
    dismissWidget: () => dismissWidget.mutate(),
    toggleMinimize: (v: boolean) => toggleMinimize.mutate(v),
  };
}
